import React, { useState } from 'react';
import axiosClient from 'http/axios-client';
import * as XLSX from 'xlsx';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Table, TableBody, TableCell, TableRow, TableHead, TextField } from '@material-ui/core';
import { useItems } from 'hooks/atoms/items-hook';
import AppLoader from 'components/basic/AppLoader';
import CollectionKeys from 'hooks/molecules/collection-keys';
import { parseDate } from 'components/business/consents/ConsentsTable';
import { FaNotesMedical } from 'react-icons/fa';
import CollectionItemHeader from 'components/layout/CollectionItemHeader';
import { LoadingButtonNoGrid } from 'components/basic/LoadingButton';
import SearchBarForLab from 'components/basic/SearchBarForLab';
import getTheme from 'theme/theme';
import { makeStyles } from '@material-ui/core/styles';
import { jsonToCSV } from 'react-papaparse';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import Edit from '@material-ui/icons/Edit';
import Save from '@material-ui/icons/Save';
import { useAlerts } from 'hooks/alerts-hooks';
import AppTable from '../../basic/AppTable';
import { filterTypes } from '../../basic/AdvancedFilters';

const positions = [
  'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10', 'A11', 'A12',
  'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'B10', 'B11', 'B12',
  'C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9', 'C10', 'C11', 'C12',
  'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8', 'D9', 'D10', 'D11', 'D12',
  'E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7', 'E8', 'E9', 'E10', 'E11', 'E12',
  'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
  'G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'G10', 'G11', 'G12',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'H7', 'H8', 'H9', 'H10', 'H11', 'H12',
];

const useStyles = makeStyles(() => {
  return {
    dialogPaper: {
      width: '100%',
      maxWidth: 650,
    },
    dialogTitle: {
      padding: '16px 24px',
      borderBottom: '1px solid #e0e0e0',
    },
    dialogContent: {
      padding: 0,
    },
    table: {
      minWidth: 300,
      marginBottom: '20px',
    },
    header: {
      backgroundColor: '#f5f5f5',
    },
    headerCell: {
      fontWeight: 'bold',
      backgroundColor: '#eeebeb',
    },
    row: {
      '&:nth-of-type(odd)': {
        backgroundColor: '#fafafa',
      },
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
      '& td': {
        borderBottom: '1px solid #000',
      },

    },
    cell: {
      padding: '12px 16px',
    },
    button: {
      backgroundColor: '#d0d2d3ff',
    },
    redButton: {
      color: '#ffffff',
      backgroundColor: 'rgb(155, 40, 11)',
      '&:hover': {
        backgroundColor: '#520202ff',
      },
    },
    greenButton: {
      color: '#ffffff',
      backgroundColor: 'rgb(112, 173, 62)',
      '&:hover': {
        backgroundColor: '#033a11ff',
      },
    },
  };
});

const downloadData = async (setDownloadAllLoading, filterQuery, selected, initialFilter) => {
  setDownloadAllLoading(true);
  const theKits = await axiosClient.get(CollectionKeys.LAB_SAMPLES_SERIALS, {
    params: {
      ...filterQuery,
      ...initialFilter,
    },
  });

  if (null == theKits) {
    setDownloadAllLoading(false);
    return;
  }

  setDownloadAllLoading(false);

  const data = [];
  theKits.forEach((kit) => {
    if (selected.includes(kit.id)) {
      let daysLeftRaw = 'No Received Date';
      const today = new Date();
      if (kit?.kit_report_delivery_date !== null) {
        const deliveryDate = new Date(kit?.kit_report_delivery_date);
        daysLeftRaw = Math.floor((deliveryDate - today) / (1000 * 60 * 60 * 24));
      }
      const daysLeft = Number.isFinite(daysLeftRaw) ? daysLeftRaw : 'No Received Date';

      const obj = {
        'Serial Number': kit.serial ?? ' ',
        'Thematic Package': kit.thematic_package?.name ?? ' ',
        // Status: kit?.registrationStatus,
        'Sample Received Date': kit?.kit_metadata?.sampleReceivedDate
          ? parseDate(kit?.kit_metadata?.sampleReceivedDate)
          : 'No Received Date',
        'Days Left': daysLeft,
        'Nucleic Acid (ng/uL)': kit?.nucleic_acid,
      };

      data.push(obj);
    }
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet['!cols'] = fitToColumn(data);

  function fitToColumn(arrayOfArray) {
    if (!arrayOfArray[0]) { // If file is empty
      return null;
    }
    return Object.keys(arrayOfArray[0]).map((key) => {
      return { wch: key.length };
    });
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, 'Lab Reports.xlsx');
};

const exportData = (selected, allKits, setExported) => {
  setExported([]);
  const finalItems = [];

  selected.forEach((item, index) => {
    for (let i = 0; i <= allKits.length; i++) {
      if (allKits[i]?.id === item) {
        finalItems.push({ id: allKits[i]?.id, SamplePlate: positions[index], SerialNumber: allKits[i]?.serial });
      }
    }
  });

  setExported(finalItems);
};

const downloadCSV = async (exported, setSelected, kitRefetch, setLoading) => {
  setLoading(true);
  // Ensure data is an array of objects (CSV requires rows)
  const dataArray = Array.isArray(exported) ? exported : [ exported ];

  // transform to final format
  const finalData = dataArray.map((item) => {
    const formattedItem = {};
    formattedItem['SamplePlate.SerialNumber'] = '';
    formattedItem['SampleInfo.Address'] = item?.SamplePlate;
    formattedItem['SampleInfo.SampleID'] = item?.SerialNumber;
    formattedItem['SampleInfo.Description'] = '';
    formattedItem['SampleInfo.Dilution'] = '';
    formattedItem['SamplePlateSelectedArea.SelectedAreaIndex'] = '';

    return formattedItem;
  });

  // Add the rest of the possitions
  const initialLength = finalData.length;
  for (let i = initialLength; i <= 96; i++) {
    finalData.push({
      'SamplePlate.SerialNumber': '',
      'SampleInfo.Address': positions[i],
      'SampleInfo.SampleID': '',
      'SampleInfo.Description': '',
      'SampleInfo.Dilution': '',
      'SamplePlateSelectedArea.SelectedAreaIndex': '',
    });
  }

  // Convert JSON → CSV
  const csv = jsonToCSV(finalData);

  // Create blob
  const blob = new Blob([ csv ], { type: 'text/csv;charset=utf-8;' });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.setAttribute('download', 'output.csv');
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  // update "Imported to Run Status to True"
  await Promise.all(exported.map(async (item) => {
    if (item.id !== undefined) {
      try {
        await axiosClient.put(`serial-numbers/run-imported-update/${ item.id }`, {
          run_imported: true,
        });
      } catch (error) {
        console.error(`Failed to update item ${ item.id }`, error);
      } finally {
        setSelected([]);
        kitRefetch();
        setLoading(false);
      }
    }
  }));
};

function LabSamplesTable() {
  const initialFilter = {
    'thematic_package.name_in': [ 'Nutritional kit', 'iDNA PGx kit', 'Vitamins kit' ],
  };
  const classes = useStyles();
  const [ filterQuery, setFilterQuery ] = useState({});
  const [ selected, setSelected ] = useState([]);
  const [ downloadLoading, setDownloadLoading ] = useState(false);
  const [ openDialog, setOpenDialog ] = useState(false);
  const [ exported, setExported ] = useState([]);
  const [ extraSerial, setExtraSerial ] = useState('');
  const [ loading, setLoading ] = useState(false);
  const [ nucLoading, setNucLoading ] = useState(false);
  const [ editNucAcidArray, setEditNucAcidArray ] = useState([]);
  const [ nucAcidValue, setNucAcidValue ] = useState(0);
  const { alertSuccess, alertError } = useAlerts();

  const {
    items: kits,
    itemsLoading: kitsLoading,
    refetch: kitRefetch,
    itemsError: kitsError,
    itemsFetching: kitsFetching,
    pagination,
  } = useItems(CollectionKeys.LAB_SAMPLES_SERIALS, { filterQuery: {
    ...filterQuery,
    ...initialFilter,
  },
  queryOptions: { refetchOnWindowFocus: false } });

  const {
    items: allKits,
    itemsLoading: allKitsLoading,
    itemsError: allKitsError,
  } = useItems(CollectionKeys.LAB_SAMPLES_SERIALS, { filterQuery: {
    ...filterQuery,
    ...initialFilter,
    _limit: -1,
  } });

  async function updateNucleicAcid(serial, nucleicAcid) {
    setNucLoading(true);
    if (serial !== undefined && /-?\d+(\.\d+)?/.test(nucleicAcid)) {
      try {
        const dataToImport = [{ serial, metadata: { nucleic_acid: nucleicAcid } }];
        await axiosClient.put('/serial-numbers/bulk-metadata-update', dataToImport);
        alertSuccess('Nucleic Acid Value Updated');
      } catch (error) {
        alertError(`${ error }`);
      }
    }
    kitRefetch();
    setNucLoading(false);
    setEditNucAcidArray([]);
    setNucAcidValue('');
  }

  if (kitsLoading || allKitsLoading || loading) {
    return <AppLoader />;
  }
  const tableColumns = [
    { id: 1, label: 'Serial Number', field: 'serial' },
    { id: 2, label: 'Thematic Package', field: 'thematic_package.name' },
    { id: 3,
      label: 'Sample Received Date',
      field: 'kit_metadata.sampleReceivedDate',
      noSort: false,
      isDate: true,
      customRenderer({ row }) {
        let Rday = 'No Received Date';
        if (undefined !== row?.kit_metadata?.sampleReceivedDate) {
          Rday = parseDate(row?.kit_metadata?.sampleReceivedDate);
        }
        return (
          <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
            {Rday}
          </div>
        );
      } },
    { id: 4,
      label: 'Days Left',
      field: 'kit_report_delivery_date',
      noSort: false,
      customRenderer({ row }) {
        let daysLeftRaw = 'No Received Date';
        const today = new Date();
        if (row?.kit_report_delivery_date !== null) {
          const deliveryDate = new Date(row?.kit_report_delivery_date);
          daysLeftRaw = Math.floor((deliveryDate - today) / (1000 * 60 * 60 * 24));
        }
        const daysLeft = Number.isFinite(daysLeftRaw) ? daysLeftRaw : 'No Received Date';
        return (
          <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
            {daysLeft}
          </div>
        );
      } },
    {
      id: 5,
      label: 'Nucleic Acid',
      noSort: true,
      field: 'nucleic_acid',
      customRenderer({ row }) {
        return (
          <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>

            {editNucAcidArray.includes(row?.serial)
              ? (
                <TextField
                  key={ row?.serial }
                  style={ { maxWidth: '100px', color: '#000000' } }
                  size='small'
                  variant='outlined'
                  name='nucleic_acid'
                  autoFocus
                  value={ nucLoading ? 'Saving...' : nucAcidValue }
                  onChange={ (e) => { setNucAcidValue(e.target.value); } }
                />
              )
              : kitsFetching ? 'Fetching' : row?.nucleic_acid || <span style={ { color: 'gray' } }>- - -  - - -</span>}

            {editNucAcidArray.length === 0 && (
              <Edit
                style={ { marginLeft: '10px', fontSize: 18, color: '#868686' } }
                onClick={ () => {
                  setEditNucAcidArray([ ...editNucAcidArray, row?.serial ]);
                  setNucAcidValue(row?.nucleic_acid || '');
                } }
              />
            )}
            {editNucAcidArray.includes(row?.serial) && (
              <>
                <Save
                  style={ { fontSize: 25, color: '#111c4e' } }
                  onClick={ () => { updateNucleicAcid(row?.serial, nucAcidValue); } }
                />
                <ClearIcon
                  style={ { fontSize: 25, color: '#a50707' } }
                  onClick={ () => { setEditNucAcidArray([]); setNucAcidValue(''); } }
                />
              </>
            )}
          </div>
        );
      },
    },
    {
      id: 6,
      label: 'Imported to Run',
      noSort: false,
      field: 'run_imported',
      customRenderer({ row }) {
        return (
          <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
            {row?.run_imported
              ? <span style={ { color: '#a50707', fontWeight: '700', backgroundColor: '#f7f455', paddingLeft: '30px', paddingRight: '30px' } }><CheckIcon /></span> : <ClearIcon />}
          </div>
        );
      },
    },
  ];

  if (kitsError) {
    return (
      <span>
        Error:
        {kitsError.message}
      </span>
    );
  }

  if (allKitsError) {
    return (
      <span>
        Error:
        {allKitsError.message}
      </span>
    );
  }

  return (
    <>
      <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: getTheme().spacing(2) } }>
        <FaNotesMedical
          style={ { color: getTheme().palette.secondary.dark, marginRight: getTheme().spacing(2) } }
          size={ 36 }
        />
        <CollectionItemHeader
          title='Lab Samples'
          subtitle='Nutritional kit, iDNA PGx kit, Vitamins kit'
          actions={ (
            <>
            </>
          ) }
        />
      </div>
      <div style={ { display: 'flex', gap: '5px', marginBottom: '15px' } }>
        <span>
          <Button
            variant='contained'
            color='secondary'
            onClick={ () => {
              setSelected(allKits?.map((ar) => { return ar.id; }));
            } }
          >
            Select All
          </Button>
        </span>
        <span>
          <Button
            variant='contained'
            color='secondary'
            onClick={ () => {
              setSelected([]);
            } }
          >
            Clear Selection
          </Button>
        </span>
        <LoadingButtonNoGrid
          variant='contained'
          onClick={ () => { downloadData(setDownloadLoading, filterQuery, selected, initialFilter); } }
          label='Excel Download'
          loading={ downloadLoading }
        />
        <LoadingButtonNoGrid
          variant='contained'
          onClick={ () => { exportData(selected, allKits, setExported); setOpenDialog(true); } }
          label='Export LAB CSV'
          loading={ downloadLoading }
        />
        <SearchBarForLab setFilterQuery={ setFilterQuery } table='LabSamples' />
      </div>
      <AppTable
        columns={ tableColumns }
        data={ kits }
        refetch={ kitRefetch }
        loading={ kitsLoading || allKitsLoading }
        paginationProps={ pagination }
        advancedFiltersProps={ {
          properties: [
            {
              value: 'thematic_package.name',
              label: 'Thematic Package',
              type: filterTypes.CATEGORY,
              options: [
                { value: 'iDNA PGx kit', label: 'PGx' },
                { value: 'Nutritional kit', label: 'Nutrition' },
                { value: 'Vitamins kit', label: 'Vitamins' },
              ],
            },
          ],
          initialAndFilterOptions: [],
          onQueryChange: setFilterQuery,
        } }
        selectionProps={ { setSelected, selected } }
      />
      <Dialog
        open={ openDialog }
        onClose={ () => { setOpenDialog(false); setExtraSerial(''); } }
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        classes={ { paper: classes.dialogPaper } }
      >
        <DialogTitle
          className={ classes.dialogTitle }
          id='alert-dialog-title'
        >
          Selected Samples for export to CSV
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Table className={ classes.table }>
              <TableHead>
                <TableCell className={ classes.headerCell }>
                  Sample Plate
                </TableCell>
                <TableCell className={ classes.headerCell }>
                  Serial Number
                </TableCell>
              </TableHead>
              <TableBody>
                {exported.map((item) => {
                  return (
                    <TableRow key={ item.SerialNumber } className={ classes.row }>
                      <TableCell className={ classes.cell }>
                        {item.SamplePlate}
                      </TableCell>
                      <TableCell className={ classes.cell }>
                        {item.SerialNumber}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div style={ { display: 'flex', gap: '5px' } }>
              Add new serial to next position:
              <TextField
                value={ extraSerial }
                onChange={ (e) => { setExtraSerial(e.target.value); } }
              />

              <Button
                className={ classes.greenButton }
                onClick={ () => {
                  setExported([ ...exported, { SamplePlate: positions[exported.length], SerialNumber: extraSerial }]);
                  setExtraSerial('');
                } }
              >
                Import
              </Button>
              <Button
                className={ classes.redButton }
                color='error'
                onClick={ () => {
                  exportData(selected, allKits, setExported);
                } }
              >
                {' '}
                Clear extra
              </Button>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            // disabled={}
            className={ classes.greenButton }
            onClick={ () => { downloadCSV(exported, setSelected, kitRefetch, setLoading); setOpenDialog(false); } }
          >
            Export
          </Button>
          <Button
            className={ classes.button }
            onClick={ () => { setExtraSerial(''); setOpenDialog(false); } }
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LabSamplesTable;
